import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHelper } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import { PagedResultDto } from '../models/paged-result.model';
import {
  FacilityInvoice,
  FacilityInvoiceDetail,
  InvoiceDto,
  InvoicePayment,
  InvoicesSummary,
  InvoiceSummaryDto,
  RecordInvoiceDto
} from '../models/payments/invoices.dto';
import {
  InvoiceGroupSummaryDto,
  InvoiceGroupDetailDto,
  InvoiceGroupSearchDto,
  PaymentAttemptResultDto,
  InvoiceGroupPaymentResultDto,
  PayInvoiceGroupRequest
} from '../models/payments/invoice-group.dto';
import { StripePaymentLink } from '../models/stripe.model';
import {
  CreateCheckoutSessionRequest,
  StripeCheckoutSession
} from '../models/payments/checkout-session.dto';

@Injectable({ providedIn: 'root' })
export class PaymentInvoicesService {
  private readonly baseUrl = 'api/Payments/invoices';

  private readonly http = inject(HttpClient);

  /**
   * Fetch invoices for a facility.
   * @param payload Optional filters like status, date range, etc.
   * @returns An Observable of paginated FacilityInvoiceDetail records.
   */
  getAll(
    payload?: Partial<InvoiceDto>
  ): Observable<PagedResultDto<FacilityInvoice>> {
    const params = HttpHelper.toHttpParams(payload ?? {});

    return this.http.get<PagedResultDto<FacilityInvoice>>(this.baseUrl, {
      params
    });
  }

  getSummary(dto?: InvoiceSummaryDto): Observable<InvoicesSummary> {
    const params = HttpHelper.toHttpParams(dto ?? {});
    return this.http.get<InvoicesSummary>(
      `${this.baseUrl}/pay-period-summary`,
      { params }
    );
  }

  /**
   * Get a single invoice by its ID.
   * @param invoiceId The ID of the invoice.
   * @returns An Observable of the Invoice.
   */
  get(invoiceId: string): Observable<FacilityInvoiceDetail> {
    return this.http.get<FacilityInvoiceDetail>(`${this.baseUrl}/${invoiceId}`);
  }

  recordPayment(
    invoiceId: string,
    body: RecordInvoiceDto
  ): Observable<InvoicePayment> {
    return this.http.post<InvoicePayment>(
      `${this.baseUrl}/${invoiceId}/payments`,
      body
    );
  }

  getPayments(invoiceId: string): Observable<InvoicePayment[]> {
    return this.http.get<InvoicePayment[]>(
      `${this.baseUrl}/${invoiceId}/payments`
    );
  }

  download(invoiceId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${invoiceId}/download`, {
      responseType: 'blob'
    });
  }

  createPaymentLink(invoiceId: string): Observable<StripePaymentLink> {
    return this.http.post<StripePaymentLink>(
      `${this.baseUrl}/${invoiceId}/payment-link`,
      {}
    );
  }

  payInvoiceWithBankAccount(
    invoiceId: string,
    paymentMethodId: string
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${invoiceId}/pay`, {
      paymentMethodId
    });
  }

  createCheckoutSession(
    invoiceId: string,
    body: CreateCheckoutSessionRequest
  ): Observable<StripeCheckoutSession> {
    return this.http.post<StripeCheckoutSession>(
      `${this.baseUrl}/${invoiceId}/checkout-session`,
      body
    );
  }

  /**
   * Pay invoice with facility's default payment method (new payment flow).
   * According to payment-changes.md §4, this endpoint handles card and ACH payments
   * using the facility's default payment method. Returns PaymentAttemptResultDto
   * with status: succeeded | processing | failed | requires_action
   * @param facilityId The facility ID
   * @param invoiceId The invoice ID
   * @returns Observable of PaymentAttemptResultDto
   */
  payInvoiceWithDefaultMethod(
    facilityId: string,
    invoiceId: string
  ): Observable<PaymentAttemptResultDto> {
    return this.http.post<PaymentAttemptResultDto>(
      `api/facilities/${facilityId}/invoices/${invoiceId}/pay`,
      {}
    );
  }

  /**
   * Get invoice groups for a corporation.
   * According to payment-changes.md §3.1, returns a paginated list of weekly invoice groups.
   * @param corporationId The corporation ID
   * @param search Optional search parameters (status, pagination, etc.)
   * @returns Observable of paginated InvoiceGroupSummaryDto records
   */
  getInvoiceGroups(
    corporationId: string,
    search?: Partial<InvoiceGroupSearchDto>
  ): Observable<PagedResultDto<InvoiceGroupSummaryDto>> {
    const params = HttpHelper.toHttpParams(search ?? {});
    return this.http.get<PagedResultDto<InvoiceGroupSummaryDto>>(
      `api/corporate/${corporationId}/invoice-groups`,
      { params }
    );
  }

  /**
   * Get details of a specific invoice group.
   * According to payment-changes.md §3.2, returns the group with all member invoices
   * and complete payment history.
   * @param corporationId The corporation ID
   * @param groupId The invoice group ID
   * @returns Observable of InvoiceGroupDetailDto with invoices and payment history
   */
  getInvoiceGroupDetail(
    corporationId: string,
    groupId: string
  ): Observable<InvoiceGroupDetailDto> {
    return this.http.get<InvoiceGroupDetailDto>(
      `api/corporate/${corporationId}/invoice-groups/${groupId}`
    );
  }

  /**
   * Pay invoice(s) in a corporate invoice group with default payment method.
   * According to payment-changes.md §3.3, this charges each invoice independently
   * against the corporation's default payment method.
   * @param corporationId The corporation ID
   * @param groupId The invoice group ID
   * @param invoiceIds Array of invoice IDs to pay (can be single or multiple)
   * @returns Observable of InvoiceGroupPaymentResultDto with successes[], processing[], failures[]
   */
  payInvoiceGroupWithDefaultMethod(
    corporationId: string,
    groupId: string,
    invoiceIds: string[]
  ): Observable<InvoiceGroupPaymentResultDto> {
    return this.http.post<InvoiceGroupPaymentResultDto>(
      `api/corporate/${corporationId}/invoice-groups/${groupId}/pay`,
      { invoiceIds } as PayInvoiceGroupRequest
    );
  }
}
